import { createAsyncThunk, createSlice } from '@reduxjs/toolkit'

interface RecipeQrgState {
  loading: boolean
  fetchingCurrent: boolean
  currentPdfUrl: string | null
  uploadedUrl: string | null
  error: unknown
  success: string | null
}

const initialState: RecipeQrgState = {
  loading: false,
  fetchingCurrent: false,
  currentPdfUrl: null,
  uploadedUrl: null,
  error: null,
  success: null
}

const extractPdfUrlFromResponse = (responseData: unknown): string | null => {
  if (!responseData || typeof responseData !== 'object') {
    return null
  }

  if ('url' in responseData && typeof (responseData as { url?: unknown }).url === 'string') {
    return (responseData as { url: string }).url
  }

  if ('data' in responseData) {
    const nested = (responseData as { data?: unknown }).data
    if (nested && typeof nested === 'object' && 'url' in nested) {
      const nestedUrl = (nested as { url?: unknown }).url

      return typeof nestedUrl === 'string' ? nestedUrl : null
    }
  }

  return null
}

export const fetchCafeHolaQrgPdf = createAsyncThunk<
  { url: string | null },
  void,
  { rejectValue: unknown; extra: { httpClient: unknown } }
>('gygRecipeQrg/fetchCafeHolaQrgPdf', async (_, { rejectWithValue, extra }) => {
  try {
    const response = await (extra.httpClient as { get: Function }).get('recipe-qrg/cafe-hola')
    const url = extractPdfUrlFromResponse(response.data)

    return { url }
  } catch (error: unknown) {
    return rejectWithValue((error as { response?: { data?: unknown } })?.response?.data ?? error)
  }
})

export const uploadRecipeQrgPdf = createAsyncThunk<
  { url: string },
  File,
  { rejectValue: unknown; extra: { httpClient: unknown } }
>('gygRecipeQrg/uploadRecipeQrgPdf', async (file, { rejectWithValue, extra }) => {
  try {
    const formData = new FormData()
    formData.append('file', file)

    const response = await (extra.httpClient as { post: Function }).post('recipe-qrg/upload', formData, {
      timeout: 120_000
    })

    const payload = response.data as { data?: { url?: string }; url?: string }
    const url = extractPdfUrlFromResponse(payload)

    if (!url) {
      return rejectWithValue('Upload succeeded but no file URL was returned')
    }

    return { url }
  } catch (error: unknown) {
    return rejectWithValue((error as { response?: { data?: unknown } })?.response?.data ?? error)
  }
})

const gygRecipeQrgSlice = createSlice({
  name: 'gygRecipeQrg',
  initialState,
  reducers: {
    clearRecipeQrgState(state) {
      state.uploadedUrl = null
      state.error = null
      state.success = null
    }
  },
  extraReducers: builder => {
    builder.addCase(fetchCafeHolaQrgPdf.pending, state => {
      state.fetchingCurrent = true
    })
    builder.addCase(fetchCafeHolaQrgPdf.fulfilled, (state, action) => {
      state.fetchingCurrent = false
      state.currentPdfUrl = action.payload.url
    })
    builder.addCase(fetchCafeHolaQrgPdf.rejected, state => {
      state.fetchingCurrent = false
      state.currentPdfUrl = null
    })
    builder.addCase(uploadRecipeQrgPdf.pending, state => {
      state.loading = true
      state.error = null
      state.success = null
      state.uploadedUrl = null
    })
    builder.addCase(uploadRecipeQrgPdf.fulfilled, (state, action) => {
      state.loading = false
      state.uploadedUrl = action.payload.url
      state.currentPdfUrl = action.payload.url
      state.success = 'QRG Cafe Hola PDF uploaded successfully'
    })
    builder.addCase(uploadRecipeQrgPdf.rejected, (state, action) => {
      state.loading = false
      state.error = action.payload
    })
  }
})

export const { clearRecipeQrgState } = gygRecipeQrgSlice.actions
export default gygRecipeQrgSlice.reducer
